/**
 * Which paragraphs a document's tables of contents list, read from the TOC fields'
 * instructions and a written package's styles: so an apply rebuilds the tables of
 * contents only when it writes a paragraph one of them lists.
 *
 * A TOC field lists a paragraph two ways. Its `\o` and `\u` switches list headings,
 * the paragraphs with an outline level (`OoxmlOutlineLevels`). Its `\t` switch lists
 * paragraphs by the name of their style, paired with the level the entry takes:
 * `TOC \o "1-3" \h \z \t "Schedule,1,RS Title Centered TOC1,1"` lists every paragraph
 * styled "Schedule" or "RS Title Centered TOC1", whose styles state no outline level.
 * 25 of the 42 tables of contents in the document corpus carry `\t`, and 13 documents
 * list a style of their body that way (2026-10-21).
 *
 * The instruction is read by the one reader of field instructions
 * (`fieldInstructionArgumentsOf`).
 */
import { flatOpcPartRoot, flatOpcPartXml, parseXmlDocumentOrNull } from "./flat-opc-parts";
import { OoxmlOutlineLevels } from "./ooxml-outline-levels";
import { MAIN_DOCUMENT_PART_NAME, STYLES_PART_NAME, WORDPROCESSINGML_NS, fieldInstructionArgumentsOf } from "./ooxml-names";

/** The field type a table of contents' instruction opens with. */
const TABLE_OF_CONTENTS_FIELD_TYPE = "TOC";

/** The switch whose value lists styles by name, each paired with its entry level. */
const STYLES_LISTED_BY_NAME_SWITCH = "\\t";

/**
 * What separates the names and levels of a `\t` list: Word writes the locale's list
 * separator, a comma, or a semicolon where the comma is the decimal mark.
 */
const LISTED_STYLE_SEPARATOR = /[,;]/;

/** An entry of a `\t` list that is a level rather than a style's name: "1", "2". */
const ENTRY_LEVEL = /^\d+$/;

export class TableOfContentsListing {
    /**
     * The style names the tables of contents list through `\t`, lower-cased, from each
     * TOC field's instruction.
     *
     * CASES, per instruction
     *   - a field of another type, or a TOC with no `\t` -> no name
     *   - a TOC with `\t`                               -> every entry of its list that is not a level
     *
     * EXAMPLE
     *   ' TOC \o "1-3" \h \z \t "Schedule,1,RS Title Centered TOC1,1" ' -> "schedule", "rs title centered toc1"
     *   ' TOC \o "1-3" \h \z \u '                                        -> none
     *
     * WHY lower-cased
     *   Word matches a listed name to a style whatever its case: "schedule" lists "Schedule".
     */
    static styleNamesListedBy(fieldInstructions: readonly string[]): Set<string> {
        return new Set(fieldInstructions.flatMap((fieldInstruction) => TableOfContentsListing.styleNamesListedByOne(fieldInstruction)));
    }

    /**
     * Whether a package about to replace part of the document holds a paragraph the
     * tables of contents list: a heading (`OoxmlOutlineLevels`), or a paragraph whose
     * style a TOC lists by name.
     *
     * CASES
     *   - the markup does not parse, or lacks its styles or main document part -> true: which
     *                                                                            paragraphs it
     *                                                                            holds cannot be
     *                                                                            read, so the
     *                                                                            tables of contents
     *                                                                            are rebuilt, as
     *                                                                            every write rebuilt
     *                                                                            them before
     *   - one of its paragraphs has an outline level                          -> true
     *   - one of its paragraphs is styled with a listed name                  -> true
     *   - neither                                                              -> false
     *
     * EXAMPLE
     *   DOC-018's title paragraph, styled "Schedule", no outline level, "schedule" listed -> true
     *   the liability cap, styled ListParagraph, nothing listed by name                 -> false
     */
    static packageHoldsListedParagraph(packageXml: string, styleNamesListedByName: ReadonlySet<string>): boolean {
        const packageDocument = parseXmlDocumentOrNull(packageXml);
        /** WHEN the markup does not parse as a whole document CONSEQUENCE its paragraphs cannot be read; it is read as holding one. */
        if (packageDocument === null) return true;
        const stylesXml = flatOpcPartXml(packageDocument, STYLES_PART_NAME);
        const mainDocumentRoot = flatOpcPartRoot(packageDocument, MAIN_DOCUMENT_PART_NAME);
        /** WHEN the package lacks the styles part or the main document part CONSEQUENCE its paragraphs cannot be read; it is read as holding one. */
        if (stylesXml === null || mainDocumentRoot === null) return true;
        const outlineLevels = OoxmlOutlineLevels.fromParts({ stylesXml });
        const packageParagraphs = Array.from(mainDocumentRoot.getElementsByTagNameNS(WORDPROCESSINGML_NS, "p"));
        return packageParagraphs.some((paragraph) => TableOfContentsListing.listsParagraph(outlineLevels, paragraph, styleNamesListedByName));
    }

    /**
     * Whether a table of contents lists one paragraph.
     *
     * CASES
     *   - it has an outline level              -> listed as a heading
     *   - its style's name is listed by `\t`   -> listed by name
     *   - neither                              -> not listed
     */
    private static listsParagraph(outlineLevels: OoxmlOutlineLevels, paragraph: Element, styleNamesListedByName: ReadonlySet<string>): boolean {
        /** WHEN the paragraph has an outline level CONSEQUENCE a table of contents lists it as a heading. */
        if (outlineLevels.levelsByParagraph([paragraph])[0] !== null) return true;
        const styleName = outlineLevels.styleNameOf(paragraph);
        /** NULL style name: the paragraph names no style, or one the styles part does not define; nothing lists it by name. */
        return styleName !== null && styleNamesListedByName.has(styleName.toLowerCase());
    }

    /**
     * The style names one instruction lists through `\t`.
     *
     * EXAMPLE
     *   arguments TOC, \o, "1-3", \h, \z, \t, "Schedule,1,RS Title Centered TOC1,1"
     *   -> `\t` at 5, its value at `5 + 1` = 6 -> "Schedule", "1", "RS Title Centered TOC1", "1"
     *   -> the entries that are not levels: "schedule", "rs title centered toc1"
     */
    private static styleNamesListedByOne(fieldInstruction: string): string[] {
        const [fieldTypeArgument, ...switchesAndValues] = fieldInstructionArgumentsOf(fieldInstruction);
        /** EMPTY: no field type, or a field that is not a table of contents. */
        if (fieldTypeArgument === undefined || fieldTypeArgument.text.toUpperCase() !== TABLE_OF_CONTENTS_FIELD_TYPE) return [];
        return switchesAndValues.flatMap((fieldArgument, argumentIndex) => {
            const listsStylesByName = !fieldArgument.quoted && fieldArgument.text.toLowerCase() === STYLES_LISTED_BY_NAME_SWITCH;
            /** The switch's value: the argument right after it, `argumentIndex + 1`. */
            const listedStyles = switchesAndValues[argumentIndex + 1];
            /** EMPTY: another switch or value, or a `\t` at the instruction's end with no list after it. */
            if (!listsStylesByName || listedStyles === undefined) return [];
            return listedStyles.text
                .split(LISTED_STYLE_SEPARATOR)
                .map((listEntry) => listEntry.trim())
                .filter((listEntry) => listEntry.length > 0 && !ENTRY_LEVEL.test(listEntry))
                .map((styleName) => styleName.toLowerCase());
        });
    }
}
